import { graphql } from 'gql.tada';
import { client } from '../client';
import { type WorkflowData } from '../../workflow';
import { type Order } from './getOrder';
import {
  buildReasonFields,
  extractReasons,
  getItemsFromOrder,
} from '../../utils/item';

export const getResolutionOptionsQuery = graphql(`
  query getResolutionOptions($input: ResolutionsInput!) {
    resolutionOptions(input: $input) {
      id
      responsibilities {
        customer
        retailer
      }
      exchanges {
        sameProduct {
          ...ProductInfo
        }
        otherProducts {
          ...ProductInfo
        }
      }
    }
  }
  fragment ProductInfo on ProductInfo {
    productId
    attributeOptions {
      name
      options
    }
    variants {
      name
      productId
      variantId
      sku
      available
      image
      attributes {
        name
        value
      }
      price {
        currency
        formattedString
        value
      }
    }
  }
`);

export const getResolutionOptionsQueryFn = ({
  workflowData,
  order,
  itemId,
}: {
  workflowData: WorkflowData | null;
  order: Order | null;
  itemId?: string;
}) => {
  if (!itemId) {
    throw new Error('Cannot resolve without a selected item');
  }
  if (!order) {
    throw new Error('Cannot resolve without order data');
  }
  if (!workflowData) {
    throw new Error('Cannot resolve without workflow data');
  }

  const items = getItemsFromOrder(workflowData.items, order);

  if (!items || items.length === 0) {
    throw new Error('Cannot resolve without item data');
  }

  const item = items.find((i) => i.id === itemId);
  const quantity = workflowData.items?.[itemId]?.itemSelection?.quantity;

  if (!item || !quantity) {
    throw new Error(
      'Cannot resolve without item data when getting resolution options',
    );
  }

  const reasonsByItem = extractReasons({
    data: workflowData,
    order,
  });
  const reasonsForItem = reasonsByItem[itemId];
  const { selectedRootReason, selectedNestedReason } = reasonsForItem ?? {
    selectedRootReason: undefined,
    selectedNestedReason: undefined,
  };

  if (!selectedRootReason) {
    throw new Error(
      'Cannot resolve without item reason data when getting resolution options',
    );
  }

  const reason = workflowData?.reason || workflowData.items?.[item.id]?.reason;

  const reasonFields = reason ? buildReasonFields(reason) : [];

  const itemInput = {
    id: item.id,
    item: {
      id: item.id,
      sku: item.sku,
      variantId: item.variant?.id,
    },
    quantity,
    reason: {
      id: selectedRootReason.code,
      title: selectedRootReason?.title,
      children: selectedNestedReason
        ? [
            {
              id: selectedNestedReason.code,
              title: selectedNestedReason.title,
            },
          ]
        : [],
      fields: reasonFields,
    },
  };

  return client.request(getResolutionOptionsQuery, {
    input: {
      item: itemInput,
      orderTokens: order.orderTokens,
    },
  });
};

/* eslint-disable typescript/no-explicit-any -- Required for gql.tada type inference */
export type ResolutionOptionInputVariables =
  typeof getResolutionOptionsQuery extends import('gql.tada').TadaDocumentNode<
    any,
    infer T,
    any
  >
    ? T
    : never;
/* eslint-enable typescript/no-explicit-any */
export type CartItemInput = ResolutionOptionInputVariables['input']['item'];

export type GetResolutionOptionsResult = Awaited<
  ReturnType<typeof getResolutionOptionsQueryFn>
>;
export type ResolutionOption =
  GetResolutionOptionsResult['resolutionOptions'][number];
export type Responsibilities = ResolutionOption['responsibilities'];
export type ProductInfo = NonNullable<
  NonNullable<ResolutionOption['exchanges']>['sameProduct']
>;
export type AttributeOptions = ProductInfo['attributeOptions'];
export type ProductVariant = ProductInfo['variants'][number];
export type Attribute = ProductVariant['attributes'][number];
